'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

// Dynamically import react-d3-tree to avoid SSR issues
const Tree = dynamic(() => import('react-d3-tree'), { ssr: false });

// Tree node data interface
interface TreeNodeData {
  name: string;
  level: number;
  hasChildren: boolean;
  isExpanded: boolean;
  attributes?: {
    level: number;
    hasChildren: boolean;
    isExpanded: boolean;
  };
  children?: TreeNodeData[];
}

// Tree data structure
const createTreeData = (): TreeNodeData => ({
  name: 'LeadPops Mortgage Marketing System',
  level: 0,
  hasChildren: true,
  isExpanded: true,
  attributes: {
    level: 0,
    hasChildren: true,
    isExpanded: true
  },
  children: [
    {
      name: 'Core Value & Problem Solved',
      level: 1,
      hasChildren: true,
      isExpanded: false,
      attributes: {
        level: 1,
        hasChildren: true,
        isExpanded: false
      },
      children: [
        {
          name: 'Generate Exclusive Leads & Automate Follow-up',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Build & Leverage Realtor Partnerships',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Compete with Big Lenders & Close Tech Gaps',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Address Ineffective Traditional Marketing & Lost Opportunities',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        }
      ]
    },
    {
      name: 'LeadPops Solution: Complete Infrastructure',
      level: 1,
      hasChildren: true,
      isExpanded: false,
      attributes: {
        level: 1,
        hasChildren: true,
        isExpanded: false
      },
      children: [
        {
          name: 'Dual Approach: Consumer Direct & Realtor Partnerships',
          level: 2,
          hasChildren: true,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: true,
            isExpanded: false
          },
          children: [
            {
              name: 'Consumer Direct: Exclusive Lead Generation (High ROI)',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Realtor Partnerships: Tools for Agents, High Conversion',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            }
          ]
        },
        {
          name: 'Key Features & Tools',
          level: 2,
          hasChildren: true,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: true,
            isExpanded: false
          },
          children: [
            {
              name: 'Mortgage Funnels (High Conversion, CRM Integration)',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Mortgage Lead Generation Websites',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Convert CRM (Sub-30 Second Response)',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Done-For-You Marketing & Realtor System',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            }
          ]
        },
        {
          name: 'Capture, Convert, Partner, Scale',
          level: 2,
          hasChildren: true,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: true,
            isExpanded: false
          },
          children: [
            {
              name: 'Intelligent Lead Capture & Fast Conversion',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Realtor Partnership & Business Scaling',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            }
          ]
        }
      ]
    },
    {
      name: 'How It Works & Implementation',
      level: 1,
      hasChildren: true,
      isExpanded: false,
      attributes: {
        level: 1,
        hasChildren: true,
        isExpanded: false
      },
      children: [
        {
          name: 'Managed Marketing Channels',
          level: 2,
          hasChildren: true,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: true,
            isExpanded: false
          },
          children: [
            {
              name: 'Google Ads, Facebook/Instagram Campaigns',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Website Development & Marketing Automation',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Partnership Marketing Tools',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            }
          ]
        },
        {
          name: 'Implementation Timeline (Weeks 1-4 & Beyond)',
          level: 2,
          hasChildren: true,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: true,
            isExpanded: false
          },
          children: [
            {
              name: 'Foundation Setup & System Activation',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Data-Driven Optimization & Scaling',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            },
            {
              name: 'Commitment: 6-12 Months, $1K-$3K/Month Budget',
              level: 3,
              hasChildren: false,
              isExpanded: false,
              attributes: {
                level: 3,
                hasChildren: false,
                isExpanded: false
              }
            }
          ]
        }
      ]
    },
    {
      name: 'Benefits & Results (Vs. Alternatives)',
      level: 1,
      hasChildren: true,
      isExpanded: false,
      attributes: {
        level: 1,
        hasChildren: true,
        isExpanded: false
      },
      children: [
        {
          name: 'Vs. Buying Shared Leads: Exclusive Leads, Lower Cost',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Vs. Generic Tools: Mortgage-Specific, Fast Setup, Compliance',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Vs. Hiring In-House: Cost-Effective, Team Expertise',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        },
        {
          name: 'Client Success: Increased Closings, Lower CPL, High ROI',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        }
      ]
    },
    {
      name: 'Next Steps: Get a Proven System',
      level: 1,
      hasChildren: true,
      isExpanded: false,
      attributes: {
        level: 1,
        hasChildren: true,
        isExpanded: false
      },
      children: [
        {
          name: 'Book a Strategy Session: Custom Plan & ROI Projection',
          level: 2,
          hasChildren: false,
          isExpanded: false,
          attributes: {
            level: 2,
            hasChildren: false,
            isExpanded: false
          }
        }
      ]
    }
  ]
});

// Custom node renderer
const renderCustomNodeElement = ({ nodeDatum, toggleNode, foreignObjectProps }: any) => {
  const level = nodeDatum.attributes?.level || 0;
  const hasChildren = nodeDatum.attributes?.hasChildren || false;
  const isExpanded = nodeDatum.attributes?.isExpanded || false;
  
  // Add this function at the top of renderCustomNodeElement
  const measureText = (text: string, fontSize: string, fontWeight: string) => {
    if (typeof document === 'undefined') return text.length * 8;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text.length * 8;
    
    context.font = `${fontWeight} ${fontSize} system-ui, -apple-system, sans-serif`;
    return context.measureText(text).width;
  };
  
  // Level-based colors
  const colors = {
    0: { bg: '#1f2937', text: '#f9fafb', border: '#374151' },
    1: { bg: '#1e40af', text: '#f0f9ff', border: '#3b82f6' },
    2: { bg: '#16a34a', text: '#f0fdf4', border: '#22c55e' },
    3: { bg: '#7c3aed', text: '#f3e8ff', border: '#a855f7' },
  };
  
  // Special CTA color for the demo link
  const isCtaNode = nodeDatum.name === 'Book a Strategy Session: Custom Plan & ROI Projection';
  const ctaColor = { bg: '#db2777', text: '#ffffff', border: '#ec4899' }; // Pink theme
  
  const color = isCtaNode ? ctaColor : (colors[level as keyof typeof colors] || colors[3]);
  const fontSize = level === 0 ? '16px' : level === 1 ? '15px' : '14px';
  const fontWeight = level === 0 ? '600' : level === 1 ? '500' : '400';
  
  // Then replace the textWidth calculation with:
  const actualTextWidth = measureText(nodeDatum.name, fontSize, fontWeight);
  const paddingHorizontal = 30; // padding on each side
  const expandIndicatorSpace = hasChildren ? 40 : 0; // space for › symbol
  const rectWidth = actualTextWidth + (paddingHorizontal * 2) + expandIndicatorSpace;
  
  // Handle click for CTA node
  const handleClick = () => {
    if (isCtaNode) {
      window.location.href = '/mortgage-lead-generation-demo';
    } else if (hasChildren) {
      toggleNode();
    }
  };
  
  if (isCtaNode) {
    return (
      <g onClick={() => window.location.href = '/mortgage-lead-generation-demo'} style={{ cursor: 'pointer' }}>
        <rect
          width={rectWidth}
          height="50"
          x={-rectWidth/2}
          y="-25"
          rx="6"
          ry="6"
          fill={color.bg}
          stroke={color.border}
          strokeWidth="1"
        />
        <text
          x="0"
          y="0"
          textAnchor="middle"
          dominantBaseline="central"
          fill={color.text}
          fontSize={fontSize}
          fontWeight={fontWeight}
          fontFamily="system-ui, -apple-system, sans-serif"
          style={{ 
            pointerEvents: 'none',
            userSelect: 'none',
            paintOrder: 'stroke',
            stroke: 'none',
            fillOpacity: 1
          }}
        >
          <tspan>{nodeDatum.name}</tspan>
        </text>
      </g>
    );
  }

  return (
    <g>
      <rect
        width={rectWidth}
        height="50"
        x={-rectWidth/2}
        y="-25"
        rx="6"
        ry="6"
        fill={color.bg}
        stroke={color.border}
        strokeWidth="1"
        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
        onClick={hasChildren ? toggleNode : undefined}
      />
      <text
        x="0"
        y="0"
        textAnchor="middle"
        dominantBaseline="central"
        fill={color.text}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily="system-ui, -apple-system, sans-serif"
        style={{ 
          pointerEvents: 'none',
          userSelect: 'none',
          paintOrder: 'stroke',
          stroke: 'none',
          fillOpacity: 1
        }}
      >
        <tspan>{nodeDatum.name}</tspan>
      </text>
      {hasChildren && (
        <text
          x={rectWidth/2 - 20}
          y="0"
          textAnchor="middle"
          dominantBaseline="central"
          fill={color.text}
          fontSize="16px"
          style={{ opacity: 0.7, pointerEvents: 'none' }}
        >
          {isExpanded ? '‹' : '›'}
        </text>
      )}
    </g>
  );
};

// Main component
function InteractiveMortgagePlatformMap() {
  const [treeData, setTreeData] = useState(createTreeData());
  const [translate, setTranslate] = useState({ x: 200, y: 450 });
  
  // Update node expansion state
  const updateNodeExpansion = (node: TreeNodeData, targetName: string, isExpanded: boolean): TreeNodeData => {
    if (node.name === targetName) {
      return {
        ...node,
        isExpanded,
        attributes: {
          level: node.level,
          hasChildren: node.hasChildren,
          isExpanded
        }
      };
    }
    
    if (node.children) {
      return {
        ...node,
        children: node.children.map(child => updateNodeExpansion(child, targetName, isExpanded))
      };
    }
    
    return node;
  };
  
  // Handle node click
  const handleNodeClick = (nodeDatum: any) => {
    // Check if this is the specific node that should navigate to a URL
    if (nodeDatum.name === 'Book a Strategy Session: Custom Plan & ROI Projection') {
      window.location.href = '/mortgage-lead-generation-demo';
      return;
    }
    
    // Only expand/collapse if node has children
    if (!nodeDatum.attributes?.hasChildren) return;
    
    const isCurrentlyExpanded = nodeDatum.attributes.isExpanded;
    const updatedTree = updateNodeExpansion(treeData, nodeDatum.name, !isCurrentlyExpanded);
    setTreeData(updatedTree);
  };
  
  return (
    <div className="mortgage-systems-diagram w-full h-[900px] bg-gray-900 rounded-xl relative overflow-hidden">
      <Tree
        data={treeData}
        orientation="horizontal"
        pathFunc="step"
        translate={translate}
        nodeSize={{ x: 500, y: 80 }}
        separation={{ siblings: 1, nonSiblings: 1.5 }}
        collapsible={true}
        initialDepth={1}
        renderCustomNodeElement={renderCustomNodeElement}
        zoom={0.8}
        enableLegacyTransitions={true}
        transitionDuration={500}
        pathClassFunc={() => 'custom-path'}
      />
      
      {/* Title and Subtitle Overlay */}
      <div className="absolute top-10 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
          Modern Mortgage Marketing System
        </h1>
        <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto opacity-90">
          Explore our system interactively
        </p>
      </div>
      
      <svg width="0" height="0">
        <defs>
          <marker
            id="white-arrow"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,6 L9,3 z" fill="#ffffff" />
          </marker>
        </defs>
      </svg>
      
      <style jsx global>{`
        /* Scoped to only affect the diagram component */
        .mortgage-systems-diagram .rd3t-tree-container {
          width: 100%;
          height: 100%;
        }
        .mortgage-systems-diagram .rd3t-svg {
          width: 100%;
          height: 100%;
        }
        .mortgage-systems-diagram .custom-path {
          stroke: #6b7280 !important;
          stroke-width: 2 !important;
          fill: none !important;
          marker-end: url(#white-arrow) !important;
        }
        .mortgage-systems-diagram .rd3t-node circle {
          display: none !important;
        }
        .mortgage-systems-diagram .rd3t-node > text {
          display: none !important;
        }
      `}</style>
      
      <div className="absolute top-6 left-6 bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-lg p-4 max-w-sm border border-gray-700">
        <h3 className="font-semibold text-gray-100 mb-3 text-sm">Interactive Mind Map</h3>
        <div className="space-y-2 text-xs text-gray-300">
          <p className="flex items-center gap-2">
            <span className="text-blue-400">›</span>
            Click nodes to expand branches
          </p>
          <p className="flex items-center gap-2">
            <span className="text-blue-400">•</span>
            Pan and zoom to navigate
          </p>
          <p className="flex items-center gap-2">
            <span className="text-blue-400">•</span>
            33 nodes total across all levels
          </p>
        </div>
      </div>
    </div>
  );
}

// Provider wrapper
export default function MortgageSystemsDiagram() {
  return (
    <ErrorBoundary fallback={<div className="text-center py-10 text-gray-400 bg-gray-900 rounded-xl">Loading platform overview...</div>}>
      <InteractiveMortgagePlatformMap />
    </ErrorBoundary>
  );
}